/**
 * The typing buffer of one site watch number field, as a reducer.
 *
 * The field shows what is typed until blur, then the clamped value it saves.
 * A stored value that changes reseeds the field. A save that fails must show
 * the stored value again: the re-read after it returns the same number, so
 * waiting for the stored value to change would leave the typed one on screen
 * while the watch runs on the old one.
 *
 * React-free: `numberDraft.test.ts` runs it under plain node.
 */

export interface NumberDraft {
  /** What the field shows. */
  text: string;
  /** The stored value the field was last seeded with. */
  stored: number;
}

export type NumberDraftAction =
  /** Typing, or the clamped value a commit puts in the field. */
  | { type: "typed"; text: string }
  /** The stored value as the card last read it. */
  | { type: "stored"; value: number }
  /** The save of `committed` failed. */
  | { type: "refused"; committed: string };

export function numberDraft(value: number): NumberDraft {
  return { text: String(value), stored: value };
}

export function numberDraftReducer(state: NumberDraft, action: NumberDraftAction): NumberDraft {
  switch (action.type) {
    case "typed":
      return state.text === action.text ? state : { ...state, text: action.text };
    case "stored":
      return action.value === state.stored ? state : numberDraft(action.value);
    case "refused":
      // Only over the value that failed: anything typed since, or a stored
      // value that changed meanwhile, already says more.
      return state.text === action.committed ? { ...state, text: String(state.stored) } : state;
  }
}
